import animation from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import { ReactComponent as SvgMenu } from '@img/icons/burger-menu.svg';
import { ReactComponent as SvgClose } from '@img/icons/cross-icon.svg';
import { ReactComponent as SvgSearch } from '@img/icons/search-icon.svg';
import { ReactComponent as SvgUser } from '@img/icons/user.svg';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import { m } from 'framer-motion';
import styled, { css } from 'styled-components';
const Container = styled.header `
  ${setCssVar('accentColor', brand.sage.sageDark)};
  ${setCssVar('bgColor', brand.white)};
  ${setCssVar('fgColor', brand.black)};

  position: absolute;
  top: 0;
  left: 0;
  display: block;
  background: ${cssVar('bgColor')};
  color: ${cssVar('fgColor')};
  width: 100%;
  z-index: ${Layers.Header};

  @media ${until(Device.DesktopSmall)} {
    padding: 10px 0 11px;
  }

  &[data-transparent='true'] {
    ${setCssVar('bgColor', 'transparent')};
  }
`;
const Grid = styled.div `
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  align-items: center;

  @media ${from(Device.DesktopSmall)} {
    grid-template-columns: repeat(24, 1fr);
    gap: 32px;

    height: 125px;
    position: relative;
  }

  @media ${from(Device.Desktop)} {
    height: 155px;
  }
`;
const SearchArea = styled.div `
  @media ${from(Device.TabletLarge)} {
    margin-right: 15px;
  }
`;
const ShowSearch = styled.button `
  background: transparent;
  border: none;
  width: 57px;
  height: 51px;
`;
const SearchIcon = styled(SvgSearch) `
  width: 30px;
  height: 30px;
  color: ${cssVar('accentColor')};
  transform: scale(1);
  transition: transform 1s ${animation.sharp}, color 1s ${animation.secondary};

  ${ShowSearch}:focus &,
  ${ShowSearch}:hover & {
    color: ${brand.sage.sageLight};
    transform: scale(1.2);
  }
`;
const UserIconButton = styled.button `
  background: transparent;
  border: none;
  width: 57px;
  height: 51px;
`;
const UserIcon = styled(SvgUser) `
  width: 30px;
  height: 30px;
  color: ${cssVar('accentColor')};
  transform: scale(1);
  transition: transform 1s ${animation.sharp}, color 1s ${animation.secondary};

  ${UserIconButton}:focus &,
  ${UserIconButton}:hover & {
    color: ${brand.sage.sageLight};
    transform: scale(1.2);
  }
`;
const HomeLink = styled.a ``;
const LogoArea = styled.div `
  grid-column: 1 / span 4;
`;
const Logo = styled.img `
  width: 106px;
  height: 32px;

  @media ${from(Device.TabletLarge)} {
    width: 180px;
    height: 55px;
  }

  @media ${from(Device.DesktopSmall)} {
    width: 250px;
    height: 76px;
    justify-self: center;
  }
`;
const ButtonHolder = styled.div `
  grid-column: 5 / span 8;
  display: flex;
  justify-content: flex-end;
  align-items: center;

  @media ${from(Device.TabletLarge)} {
    margin-right: 80px;
  }

  @media ${from(Device.DesktopSmall)} {
    grid-column: 5 / span 20;
    margin-right: 0;
  }
`;
const CtaArea = styled.div `
  display: flex;
  justify-content: flex-end;
`;
const CtaInner = styled.div `
  @media ${until(Device.TabletLarge)} {
    display: none;
  }
`;
const CtaMobileInner = styled.div `
  margin-right: 75px;

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const CtaHolder = styled.div `
  display: flex;
  justify-content: space-between;

  ${ButtonStyles.Button} {
    margin-left: 20px;

    &:first-child {
      margin-left: 0;
    }
  }
`;
const MobileBtnBar = styled.span `
  display: block;
  width: 28px;
  height: 2px;
  background-color: ${cssVar('fgColor')};
  position: absolute;
  top: 50%;
  transform-origin: center;

  &:first-child {
    transform: translateY(-50%);
    transition: transform 0.3s;
  }

  &:nth-child(2) {
    background-color: transparent;

    &:before {
      content: '';
      position: absolute;
      top: -10px;
      display: block;
      width: 28px;
      height: 2px;
      background-color: ${cssVar('fgColor')};
      transition: opacity 0.3s;
    }

    &:after {
      content: '';
      position: absolute;
      top: 8px;
      display: block;
      width: 28px;
      height: 2px;
      background-color: ${cssVar('fgColor')};
      transition: opacity 0.3s;
    }
  }

  &:last-child {
    transition: transform 0.3s;
    transform: translateY(-50%);
  }
`;
const MenuIcon = styled(SvgMenu) `
  width: 30px;
  height: 30px;
  color: ${cssVar('accentColor')};
`;
const CloseIcon = styled(SvgClose) `
  width: 30px;
  height: 30px;
  color: ${cssVar('accentColor')};
`;
const MobileMenuBtn = styled.button `
  background-color: ${brand.toast.toastLight};
  border: none;
  color: ${cssVar('fgColor')};
  width: 75px;
  height: 75px;
  position: fixed;
  top: 0;
  right: 0;
  z-index: 100;

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }

  ${({ openMenu }) => openMenu &&
    css `
      ${MobileBtnBar} {
        &:first-child {
          transform: translateY(-50%) rotate(-45deg);
        }

        &:nth-child(2) {
          &:before,
          &:after {
            opacity: 0;
          }
        }

        &:last-child {
          transform: translateY(-50%) rotate(45deg);
        }
      }
    `}
`;
const MobileNavHolder = styled(m.div) `
  position: relative;
  display: flex;
  background: ${brand.sage.sageDark};
  height: 45px;

  @media ${until(Device.DesktopSmall)} {
    position: fixed;
    top: 75px;
    left: 0;
    display: block;
    background: ${brand.sage.sageDark};
    height: calc(100vh - 75px);
    overflow-y: auto;
    width: 100%;
    z-index: 8001;
  }

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const AccessibilityBar = styled.div `
  background-color: ${brand.sage.sageDark};
  padding: 8px 12px;

  @media ${until(Device.DesktopSmall)} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;
  }

  @media ${from(Device.DesktopSmall)} {
    text-align: right;
  }
`;
export default {
    AccessibilityBar,
    ButtonHolder,
    CloseIcon,
    Container,
    CtaArea,
    CtaHolder,
    CtaInner,
    CtaMobileInner,
    Grid,
    HomeLink,
    Logo,
    LogoArea,
    MenuIcon,
    MobileBtnBar,
    MobileMenuBtn,
    MobileNavHolder,
    SearchArea,
    SearchIcon,
    ShowSearch,
    UserIcon,
    UserIconButton
};
